import type { TranslationKey } from "@privacy/translations";
import { useEffect, useId } from "react";
import type { FC } from "react";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import { IconButton, Tooltip } from "@mui/material";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@privacy/translations";
import type { Control, UseControllerProps } from "react-hook-form";
import { useController, useForm } from "react-hook-form";
import { DynamicShared } from "../../../dynamicShared";
import type {
  TemplateFormSettings,
  TemplateFormQuestion,
  UpdateFormQuestion,
  CustomizedFields,
} from "../../../api/templateFormSettings.types";
import * as FormDialog from "../../../shared/FormDialog";
import { useUpdateTemplateFormQuestion } from "../../../api/templateFormSettings";
import { RotateCcwIcon } from "../../../shared/icons";
import { Drawer } from "../../../shared/Drawer";
import { getInputTypeInfo } from "./utils";

type TemplateFormDrawerProps = {
  formId: TemplateFormSettings["id"];
  question: Exclude<TemplateFormQuestion, "followUps">;
  onClose: () => void;
};

const getDefaultValues = (question: TemplateFormQuestion): CustomizedFields => {
  const { customizedFields } = question;
  return {
    questionText: customizedFields?.questionText || question.questionText,
    description: customizedFields?.description || question.description,
    extraHelpInformation: customizedFields?.extraHelpInformation || "",
  };
};

export const TEMPLATE_FROM_DRAWER_WIDTH = "665px";
export const TemplateFormDrawer: FC<TemplateFormDrawerProps> = ({ formId, question, onClose }) => {
  const { t } = useTranslation();
  const { mutateAsync: updateFormSettings, isPending, status } = useUpdateTemplateFormQuestion();

  const { control, handleSubmit, formState, reset } = useForm<CustomizedFields>();
  const { dirtyFields, isDirty } = formState;

  useEffect(() => {
    reset(getDefaultValues(question), { keepDirtyValues: true });
  }, [question, reset]);

  const onSave = ({ questionText, description, extraHelpInformation }: CustomizedFields) => {
    const updatedFromQuestions: UpdateFormQuestion[] = [];
    if (dirtyFields.questionText)
      updatedFromQuestions.push({ op: "replace", path: `/questionText`, value: questionText || null });
    if (dirtyFields.description)
      updatedFromQuestions.push({ op: "replace", path: `/description`, value: description || null });
    if (dirtyFields.extraHelpInformation)
      updatedFromQuestions.push({ op: "replace", path: `/extraHelpInformation`, value: extraHelpInformation || null });

    updateFormSettings({
      id: formId,
      questionId: question.id,
      payload: updatedFromQuestions,
    });
  };
  return (
    <Drawer width={TEMPLATE_FROM_DRAWER_WIDTH}>
      {(() => {
        if (!question) return <Drawer.Loading />;

        const inputTypeInfo = getInputTypeInfo(question.inputType);
        if (!inputTypeInfo) return null;
        const { textKey } = inputTypeInfo;

        return (
          <>
            <Drawer.Header title={question.header} onClose={onClose} status={status} />
            <Stack sx={{ py: 3, px: Drawer.PADDING_X }}>
              <Stack direction="row" spacing={3} sx={{ alignItems: "center" }}>
                <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14), fontWeight: "600" }}>
                  {t("TemplatePage.GridHeader.QuestionType")}
                </Typography>

                <DynamicShared.Chip label={t(textKey)} variant="choice" />
              </Stack>
            </Stack>
            <Box sx={{ width: "100%" }}>
              <Divider />
            </Box>
            <Stack sx={{ py: 2, px: Drawer.PADDING_X }}>
              <FormDialog.Form onSubmit={handleSubmit(onSave)}>
                <FormDialog.Fields>
                  <TextFieldWithLabel
                    control={control}
                    name="questionText"
                    labelKey="TemplatePage.Drawer.QuestionText.Label"
                    defaultValue={question.questionText}
                    minRows={3}
                    rules={{ required: t("TemplatePage.Drawer.ValidationRequired") }}
                  />

                  <TextFieldWithLabel
                    labelKey="TemplatePage.GridHeader.InstructionText"
                    rules={{ required: t("TemplatePage.Drawer.ValidationRequired") }}
                    defaultValue={question.description}
                    minRows={3}
                    control={control}
                    name="description"
                  />

                  <Stack>
                    <FormDialog.Label htmlFor="extraHelpInformation">
                      {t("TemplatePage.GridHeader.HelpInformation")}
                    </FormDialog.Label>
                    <Typography variant="body2">{question.helpInformation}</Typography>
                  </Stack>

                  <TextFieldWithLabel
                    labelKey="TemplatePage.Drawer.ExtraHelpInformation.Label"
                    control={control}
                    defaultValue=""
                    minRows={6}
                    name="extraHelpInformation"
                  />
                </FormDialog.Fields>

                <FormDialog.ActionButtons>
                  <FormDialog.CancelButton onClick={onClose} />
                  <FormDialog.SubmitButton isSubmitting={isPending} isDisabled={!isDirty}>
                    {t("Template.Dialog.SaveButton")}
                  </FormDialog.SubmitButton>
                </FormDialog.ActionButtons>
              </FormDialog.Form>
            </Stack>
          </>
        );
      })()}
    </Drawer>
  );
};

type TextFieldWithLabelProps = {
  labelKey: TranslationKey;
  control: Control<CustomizedFields>;
  name: keyof CustomizedFields;
  defaultValue: string;
  rules?: UseControllerProps<CustomizedFields>["rules"];
  minRows: number;
};

const TextFieldWithLabel = ({ labelKey, control, name, rules, defaultValue, minRows }: TextFieldWithLabelProps) => {
  const { t } = useTranslation();
  const id = useId();

  const {
    field: { ref, ...field },
    fieldState,
  } = useController({ control, name, rules });

  return (
    <FormDialog.Field errorMessage={fieldState.error && t("TemplatePage.Drawer.ValidationRequired")}>
      <FormDialog.Label htmlFor={id}>
        {t(labelKey)}

        <Tooltip title={t("TemplatePage.Drawer.ResetQuestionText")} placement="top">
          <IconButton
            aria-label={t("TemplatePage.Drawer.ResetQuestionText")}
            sx={{ ml: 1, visibility: defaultValue !== field.value ? "visible" : "hidden" }}
            onClick={() => field.onChange(defaultValue)}
          >
            <RotateCcwIcon size={16} />
          </IconButton>
        </Tooltip>
      </FormDialog.Label>

      <TextField
        error={Boolean(fieldState.error)}
        size="small"
        fullWidth
        multiline={true}
        minRows={minRows}
        id={id}
        {...field}
        inputRef={ref}
      />
    </FormDialog.Field>
  );
};
